/**
 * Zoom limits of the overview map. Web Mercator maths only (no Leaflet), so it can be unit tested.
 * Zooms are whole numbers: markercluster only works on whole zoom levels.
 */
import type { Bounds } from "./tiles.ts";

const TILE = 256;

const mercY = (latDeg: number) => Math.log(Math.tan(Math.PI / 4 + (latDeg * Math.PI) / 360));

/** Pixel size of a lat/lon box at a whole zoom level (256 px tiles, Web Mercator). */
export function boundsPixels(b: Bounds, zoom: number): { w: number; h: number } {
  const world = TILE * 2 ** zoom;
  return {
    w: ((b[1][1] - b[0][1]) / 360) * world,
    h: ((mercY(b[1][0]) - mercY(b[0][0])) / (2 * Math.PI)) * world,
  };
}

export interface Size {
  w: number;
  h: number;
}

/**
 * The lowest zoom at which the viewport lies completely inside `coverage` (no blank margin): the
 * covered box is at least as wide and as tall as the viewport.
 */
export function coverZoom(viewport: Size, coverage: Bounds, minZoom: number, maxZoom: number) {
  for (let z = minZoom; z < maxZoom; z++) {
    const c = boundsPixels(coverage, z);
    if (c.w >= viewport.w && c.h >= viewport.h) return z;
  }
  return maxZoom;
}

/** The highest zoom at which `box` fits the viewport minus `pad` (px of floating UI in total). */
export function fitZoom(viewport: Size, box: Bounds, pad: Size, minZoom: number, maxZoom: number) {
  let best = minZoom;
  for (let z = minZoom; z <= maxZoom; z++) {
    const c = boundsPixels(box, z);
    if (c.w + pad.w <= viewport.w && c.h + pad.h <= viewport.h) best = z;
  }
  return best;
}

/** Viewports at least this wide (the desktop layout) may show the whole country with blank margins. */
export const DESKTOP_MIN_WIDTH = 900;

/**
 * The map's minimum zoom. It "covers" the viewport with swisstopo tiles, so a portrait phone never
 * shows blank bands above and below Switzerland. Exception: a desktop window shows the whole
 * country where it fits, one zoom below covering at most (a blank margin beside the coverage is better than cutting the country off).
 */
export function minZoomFor(
  viewport: Size,
  coverage: Bounds,
  country: Bounds,
  pad: Size,
  limits: { min: number; max: number },
): number {
  const cover = coverZoom(viewport, coverage, limits.min, limits.max);
  if (viewport.w < DESKTOP_MIN_WIDTH) return cover;
  // at most one zoom below covering, so the blank margin stays modest
  return Math.max(
    cover - 1,
    Math.min(cover, fitZoom(viewport, country, pad, limits.min, limits.max)),
  );
}
